import React, { useState, useRef } from 'react';
import { Upload, File, X, CheckCircle, AlertCircle, Eye } from 'lucide-react';
import toast from 'react-hot-toast';

const DocumentUpload = ({
  label,
  description,
  acceptedTypes = '.pdf,.jpg,.jpeg,.png',
  maxSize = 5, // MB
  value,
  onChange,
  required = false,
  error
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    // Validate file type
    const allowedTypes = acceptedTypes.split(',').map(type => type.trim());
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(fileExtension)) {
      toast.error(`Please upload a file with one of these extensions: ${acceptedTypes}`);
      return;
    }

    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      toast.error(`File size must be less than ${maxSize}MB`);
      return;
    }

    try {
      setUploading(true);
      
      // Create FormData for upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'document');

      // Upload to Cloudflare (or your preferred service)
      // For now, we'll simulate the upload and store file info
      const uploadResult = await simulateUpload(file);
      
      // Call onChange with the upload result
      onChange({
        name: file.name,
        size: file.size,
        type: file.type,
        url: uploadResult.url,
        uploadedAt: new Date().toISOString(),
        status: 'uploaded'
      });

      toast.success('Document uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // Simulate upload - replace with actual Cloudflare upload
  const simulateUpload = (file) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({
          url: URL.createObjectURL(file), // Temporary URL for preview
          publicId: `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
        });
      }, 1500);
    });
  };

  const handleRemove = () => {
    onChange(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handlePreview = () => {
    if (value?.url) {
      window.open(value.url, '_blank');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-3">
      {/* Label */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-semibold text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        {description && (
          <span className="text-xs text-gray-500">{description}</span>
        )}
      </div>

      {/* Upload Area */}
      {!value ? (
        <div
          className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all duration-200 ${
            dragActive
              ? 'border-primary-500 bg-primary-50'
              : uploading
              ? 'border-gray-300 bg-gray-50'
              : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedTypes}
            onChange={handleFileSelect}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={uploading}
          />
          
          <div className="space-y-3">
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                <p className="text-sm text-gray-600">Uploading document...</p>
              </>
            ) : (
              <>
                <Upload className="w-8 h-8 text-gray-400 mx-auto" />
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    Drop your document here, or{' '}
                    <span className="text-primary-600 hover:text-primary-700 cursor-pointer">
                      browse
                    </span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Supports: {acceptedTypes} (Max {maxSize}MB)
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        /* Uploaded File Display */
        <div className="border-2 border-green-200 bg-green-50 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {value.name}
                </p>
                <p className="text-xs text-gray-500">
                  {formatFileSize(value.size)} • Uploaded {new Date(value.uploadedAt).toLocaleDateString()}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={handlePreview}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                title="Preview document"
              >
                <Eye className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={handleRemove}
                className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                title="Remove document"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-600 font-medium flex items-center">
          <AlertCircle className="w-4 h-4 mr-2" />
          {error}
        </p>
      )}
    </div>
  );
};

export default DocumentUpload;
